import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft } from "lucide-react";
import api from "../api/axios";
import { ACTIVE_PRODUCT_PLANS, ACTIVE_SUBSCRIPTION_PLANS, formatPrice } from "../config/pricing";
import QRScanner from "../components/QRScanner";
import BrandLogo from "../components/shared/BrandLogo";
import LanguageSwitcher from "../components/shared/LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";

const STEPS = {
  PLAN_SELECT: 0,
  ADDRESS_FORM: 1,
  QR_LINK: 1.5,
  PAYMENT: 2,
  SUCCESS: 3,
};

// Payment method icons (inline SVG for zero-dependency)
const UpiIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 12L11 9L11 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M13 12L16 9L16 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CardIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="1.5" />
    <line x1="2" y1="9" x2="22" y2="9" stroke="currentColor" strokeWidth="1.5" />
    <line x1="6" y1="14" x2="10" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="6" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const CodIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="2" y="6" width="20" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="5.5" cy="12" r="1" fill="currentColor" />
    <circle cx="18.5" cy="12" r="1" fill="currentColor" />
  </svg>
);

const STEP_TITLES_PRODUCT = ["Choose Plan", "Shipping Details", "Payment"];
const STEP_TITLES_PREMIUM = ["Choose Plan", "Link QR Code", "Payment"];

const OrderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  useLanguage(); // context subscription
  const initialPlan = location.state?.initialPlan || null;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
  } = useForm();

  const [step, setStep] = useState(STEPS.PLAN_SELECT);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [error, setError] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [planView, setPlanView] = useState("one_time");

  // QR Link step state
  const [qrInput, setQrInput] = useState("");
  const [qrValidating, setQrValidating] = useState(false);
  const [qrValidation, setQrValidation] = useState(null);
  const [showQRScanner, setShowQRScanner] = useState(false);

  const isSubscription = selectedPlan?.type === "subscription";

  // Fetch payment config on mount
  const fetchPaymentConfig = useCallback(async () => {
    try {
      const res = await api.get("/payment/config");
      setPaymentConfig(res.data);
    } catch {
      setPaymentConfig({
        razorpayAvailable: false,
        razorpayKeyId: null,
        isTestMode: false,
        codAvailable: true,
        supportedMethods: { upi: true, card: true, netbanking: true, wallet: true, cod: true },
      });
    }
  }, []);

  useEffect(() => {
    fetchPaymentConfig();
  }, [fetchPaymentConfig]);

  // When opened with a preselected plan
  useEffect(() => {
    if (initialPlan) {
      setSelectedPlan(initialPlan);
      if (initialPlan.type === "subscription") {
        setPlanView("premium");
        setStep(STEPS.QR_LINK);
      } else {
        setStep(STEPS.ADDRESS_FORM);
      }
    }
  }, [initialPlan]);

  const validateQrCode = async (qrId) => {
    const id = (typeof qrId === "string" ? qrId : qrInput).trim().toUpperCase();
    if (!id) {
      setError("Please enter a QR Code ID");
      return;
    }
    if (!/^[A-Z0-9]{4,20}$/.test(id)) {
      setError("Invalid QR Code ID format — letters and numbers only");
      return;
    }

    setQrValidating(true);
    setError(null);
    setQrValidation(null);

    try {
      const res = await api.get(`/subscription/validate-qr/${id}`);
      setQrValidation(res.data);

      if (res.data.hasActiveSubscription) {
        setError(res.data.message);
        return;
      }

      // For family_yearly, check if family group has enough members
      if (selectedPlan?.id === "family_yearly") {
        if (!res.data.familyInfo) {
          setError("This QR code is not part of a family group. Please choose Individual Premium instead.");
          setQrValidation({ ...res.data, suggestIndividual: true });
          return;
        }
        if (res.data.familyInfo.membersWithQr < 4) {
          setError(
            `Family Premium requires 4 linked QR codes. This family group has ${res.data.familyInfo.membersWithQr}. Please add more members or choose Individual Premium.`
          );
          setQrValidation({ ...res.data, suggestIndividual: true });
          return;
        }
      }
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to validate QR code";
      setError(msg);
    } finally {
      setQrValidating(false);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    if (plan.type === "subscription") {
      setStep(STEPS.QR_LINK);
    } else {
      setStep(STEPS.ADDRESS_FORM);
    }
    setError(null);
  };

  const handleBack = () => {
    if (step === STEPS.PLAN_SELECT) {
      navigate("/");
      return;
    }
    if (step === STEPS.ADDRESS_FORM) {
      setStep(STEPS.PLAN_SELECT);
      setSelectedPlan(null);
    } else if (step === STEPS.QR_LINK) {
      setStep(STEPS.PLAN_SELECT);
      setSelectedPlan(null);
      setQrInput("");
      setQrValidation(null);
    } else if (step === STEPS.PAYMENT) {
      if (isSubscription) {
        setStep(STEPS.QR_LINK);
      } else {
        setStep(STEPS.ADDRESS_FORM);
      }
    }
    setError(null);
  };

  const handleQrLinkContinue = () => {
    if (!qrValidation?.valid || qrValidation?.hasActiveSubscription) return;
    if (qrValidation?.suggestIndividual) return;
    setStep(STEPS.PAYMENT);
    setError(null);
  };

  const handleSwitchToIndividual = () => {
    const individualPlan = ACTIVE_SUBSCRIPTION_PLANS.find((p) => p.id === "premium_yearly");
    if (individualPlan) {
      setSelectedPlan(individualPlan);
      setError(null);
      setQrValidation(null);
      if (qrInput) {
        validateQrCode(qrInput);
      }
    }
  };

  // Load Razorpay checkout script dynamically
  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  // Handle Razorpay checkout
  const handleRazorpayPayment = async (orderId, razorpayOrderId, keyId, amount, customerData, preferredMethod) => {
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setError("Failed to load payment gateway. Please try again or choose Cash on Delivery.");
      setPaymentProcessing(false);
      return;
    }

    const methodConfig = {};
    if (preferredMethod === "upi") {
      methodConfig.upi = true;
      methodConfig.card = false;
      methodConfig.netbanking = false;
      methodConfig.wallet = false;
      methodConfig.paylater = false;
    } else if (preferredMethod === "card") {
      methodConfig.upi = false;
      methodConfig.card = true;
      methodConfig.netbanking = true;
      methodConfig.wallet = false;
      methodConfig.paylater = false;
    }

    const options = {
      key: keyId,
      amount: amount * 100,
      currency: "INR",
      name: "Emergency QR",
      description: `${selectedPlan.name} — ${selectedPlan.description}`,
      image: "/logo-emergency-qr.png?v=4",
      order_id: razorpayOrderId,
      prefill: {
        name: customerData.name,
        email: customerData.email,
        contact: customerData.phone ? `+91${customerData.phone}` : undefined,
      },
      config: {
        display: {
          blocks: {
            banks: {
              name: "Pay using",
              instruments: preferredMethod === "upi"
                ? [{ method: "upi" }]
                : preferredMethod === "card"
                  ? [{ method: "card" }, { method: "netbanking" }]
                  : [{ method: "upi" }, { method: "card" }, { method: "netbanking" }],
            },
          },
          sequence: ["block.banks"],
          preferences: {
            show_default_blocks: preferredMethod ? false : true,
          },
        },
      },
      method: Object.keys(methodConfig).length > 0 ? methodConfig : undefined,
      theme: {
        color: "#DC2626",
        backdrop_color: "rgba(0,0,0,0.6)",
      },
      handler: async function (response) {
        try {
          if (isSubscription) {
            await api.post("/subscription/purchase", {
              qrId: qrInput.trim().toUpperCase(),
              planType: selectedPlan.id,
              paymentMethod: "razorpay",
            });
            setOrderResult({
              success: true,
              message: `${selectedPlan.name} subscription activated for QR Code ${qrInput.toUpperCase()}!`,
              paymentId: response.razorpay_payment_id,
            });
          } else {
            await api.post("/payment/verify", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              orderId,
            });
            setOrderResult({
              success: true,
              message: `Payment successful! We'll ship your ${selectedPlan.name} QR code sticker to your address shortly.`,
              paymentId: response.razorpay_payment_id,
            });
          }
          setStep(STEPS.SUCCESS);
          reset();
        } catch {
          setError("Payment verification failed. If amount was deducted, it will be refunded. Please contact support.");
        }
        setPaymentProcessing(false);
      },
      modal: {
        ondismiss: function () {
          setPaymentProcessing(false);
        },
        confirm_close: true,
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.on("payment.failed", function (response) {
      setError(`Payment failed: ${response.error.description || "Please try again."}`);
      setPaymentProcessing(false);
    });
    rzp.open();
  };

  // Process payment based on selected method
  const processPayment = async (paymentMethod) => {
    setError(null);
    setPaymentProcessing(true);
    setSelectedPaymentMethod(paymentMethod);

    try {
      if (isSubscription) {
        const purchaseRes = await api.post("/subscription/purchase", {
          qrId: qrInput.trim().toUpperCase(),
          planType: selectedPlan.id,
          paymentMethod: paymentMethod === "cod" ? "cod" : "razorpay",
        });

        if (paymentMethod === "cod") {
          setOrderResult({
            success: true,
            message: purchaseRes.data.message || `${selectedPlan.name} subscription activated for QR Code ${qrInput.toUpperCase()}!`,
          });
          setStep(STEPS.SUCCESS);
          reset();
          setPaymentProcessing(false);
          return;
        }

        setOrderResult({
          success: true,
          message: purchaseRes.data.message || `${selectedPlan.name} subscription activated!`,
        });
        setStep(STEPS.SUCCESS);
        reset();
        setPaymentProcessing(false);
        return;
      }

      // Product purchase
      const formData = getValues();

      if (paymentMethod === "cod") {
        const orderRes = await api.post("/order", {
          ...formData,
          addressLine2: formData.addressLine2 || "",
          addressLine3: formData.addressLine3 || "",
          landmark: formData.landmark || "",
          planType: selectedPlan.id,
          paymentMethod: "cod",
        });

        setOrderResult({
          success: true,
          message: `Order placed! Pay ${formatPrice(selectedPlan.price)} when you receive it. We'll ship your ${selectedPlan.name} QR code sticker soon.`,
          orderId: orderRes.data.order.id,
        });
        setStep(STEPS.SUCCESS);
        reset();
        setPaymentProcessing(false);
        return;
      }

      // UPI or Card - use Razorpay
      if (!paymentConfig?.razorpayAvailable) {
        setError("Online payment is being set up. Please use Cash on Delivery for now.");
        setPaymentProcessing(false);
        return;
      }

      const paymentRes = await api.post("/payment/create-order", {
        planType: selectedPlan.id,
        customerName: formData.name,
        customerPhone: formData.phone,
        customerEmail: formData.email,
      });

      if (paymentRes.data.paymentMethod !== "razorpay") {
        setError("Online payment temporarily unavailable. Please use Cash on Delivery.");
        setPaymentProcessing(false);
        return;
      }

      const orderRes = await api.post("/order", {
        ...formData,
        addressLine2: formData.addressLine2 || "",
        addressLine3: formData.addressLine3 || "",
        landmark: formData.landmark || "",
        planType: selectedPlan.id,
        paymentMethod: "razorpay",
        razorpayOrderId: paymentRes.data.razorpayOrderId,
      });

      handleRazorpayPayment(
        orderRes.data.order.id,
        paymentRes.data.razorpayOrderId,
        paymentRes.data.razorpayKeyId,
        paymentRes.data.amount,
        formData,
        paymentMethod
      );
    } catch (err) {
      const errorMessage = err.response?.data?.error || "Failed to process payment. Please try again.";
      setError(errorMessage);
      setPaymentProcessing(false);
    }
  };

  const stepTitles = isSubscription ? STEP_TITLES_PREMIUM : STEP_TITLES_PRODUCT;
  const currentStepIndex = step === STEPS.QR_LINK ? 1 : step > STEPS.QR_LINK ? 2 : step;

  // Payment method options
  const paymentMethods = isSubscription
    ? [
        {
          id: "upi",
          label: "UPI",
          sublabel: "Google Pay, PhonePe, Paytm, BHIM",
          icon: <UpiIcon />,
          recommended: true,
        },
        {
          id: "card",
          label: "Credit / Debit Card",
          sublabel: "Visa, Mastercard, Rupay, Amex",
          icon: <CardIcon />,
          recommended: false,
        },
      ]
    : [
        {
          id: "upi",
          label: "UPI",
          sublabel: "Google Pay, PhonePe, Paytm, BHIM",
          icon: <UpiIcon />,
          recommended: true,
        },
        {
          id: "card",
          label: "Credit / Debit Card",
          sublabel: "Visa, Mastercard, Rupay, Amex",
          icon: <CardIcon />,
          recommended: false,
        },
        {
          id: "cod",
          label: "Cash on Delivery",
          sublabel: "Pay when you receive the package",
          icon: <CodIcon />,
          recommended: false,
        },
      ];

  const inputBase = "w-full px-3 py-2.5 border rounded-lg text-base focus:outline-none focus:ring-2 transition-colors duration-150";
  const inputNormal = `${inputBase} border-slate-300 focus:ring-red-200 focus:border-red-400`;
  const inputError = `${inputBase} border-red-400 focus:ring-red-200 focus:border-red-400`;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation bar */}
      <nav className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors duration-150"
              aria-label="Go back"
            >
              <ArrowLeft size={20} />
            </button>
            <BrandLogo />
          </div>
        </div>
      </nav>

      {/* Main content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-4 sm:p-6">
          {/* Header */}
          <div className="flex items-center gap-2 mb-4">
            {step > STEPS.PLAN_SELECT && step < STEPS.SUCCESS && (
              <button
                onClick={handleBack}
                disabled={paymentProcessing}
                className={`text-lg text-slate-500 p-1 transition-colors duration-150 ${paymentProcessing ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:text-slate-700"}`}
                aria-label="Go back"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h1 className="text-xl font-bold text-slate-900">
              {step === STEPS.PLAN_SELECT && "Choose Your Plan"}
              {step === STEPS.ADDRESS_FORM && "Shipping Details"}
              {step === STEPS.QR_LINK && "Link Your QR Code"}
              {step === STEPS.PAYMENT && "Choose Payment Method"}
              {step === STEPS.SUCCESS && (isSubscription ? "Subscription Activated!" : "Order Confirmed!")}
            </h1>
          </div>

          {/* Step indicator */}
          {step < STEPS.SUCCESS && (
            <p className="text-sm text-slate-500 font-medium mb-4">
              Step {currentStepIndex + 1} of 3: {stepTitles[currentStepIndex]}
            </p>
          )}

          {/* Error display */}
          {error && (
            <div className="bg-red-50 border-2 border-red-500 rounded-lg p-3 mb-3">
              <p className="text-red-600 text-sm font-semibold m-0">{error}</p>
            </div>
          )}

          {/* STEP 1: Plan Selection */}
          {step === STEPS.PLAN_SELECT && (
            <div>
              {/* Toggle: One-time | Premium */}
              <div className="flex p-1 rounded-lg bg-slate-200/80 border border-slate-200 mb-4">
                <button
                  type="button"
                  onClick={() => setPlanView("one_time")}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-colors duration-150 ${planView === "one_time" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-800"}`}
                >
                  QR Codes & Stickers
                </button>
                <button
                  type="button"
                  onClick={() => setPlanView("premium")}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold transition-colors duration-150 ${planView === "premium" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-800"}`}
                >
                  Premium Membership
                </button>
              </div>

              <p className="text-slate-500 text-sm mb-4 leading-relaxed">
                {planView === "one_time"
                  ? "Select a plan to get started. All plans include 1 year of premium features free."
                  : "Unlock advanced features: scan notifications, document upload, offline access, and priority support."}
              </p>
              <div className="flex flex-col gap-3">
                {(planView === "one_time" ? ACTIVE_PRODUCT_PLANS : ACTIVE_SUBSCRIPTION_PLANS).map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => handlePlanSelect(plan)}
                    className={`relative flex flex-col p-3 rounded-lg border-2 text-left transition-colors duration-150 cursor-pointer hover:shadow-md ${
                      plan.badge
                        ? "border-red-500 bg-red-50/30 hover:border-red-600"
                        : "border-slate-200 bg-white hover:border-red-500"
                    }`}
                  >
                    {plan.badge && (
                      <span className="absolute -top-2.5 right-4 bg-red-600 text-white px-3 py-0.5 rounded-lg text-[11px] font-bold uppercase tracking-wide">
                        {plan.badge}
                      </span>
                    )}
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 mb-0.5">{plan.name}</h3>
                        <p className="text-xs text-slate-500">{plan.description}</p>
                      </div>
                      <div className="text-right flex-shrink-0 ml-3">
                        <span className="text-2xl font-extrabold text-slate-900">
                          {formatPrice(plan.price)}
                        </span>
                        <p className="text-[11px] text-slate-400">{planView === "one_time" ? "one-time" : "/year"}</p>
                      </div>
                    </div>
                    {plan.features && (
                      <ul className="list-none p-0 mt-2 flex flex-wrap gap-1.5">
                        {plan.features.map((feat, i) => (
                          <li
                            key={i}
                            className="text-xs text-slate-600 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-100"
                          >
                            {feat}
                          </li>
                        ))}
                      </ul>
                    )}
                  </button>
                ))}
              </div>

              {/* Free shipping note (only for one-time) */}
              {planView === "one_time" && (
                <div className="mt-4 p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                  <p className="text-xs text-green-700 font-semibold m-0">
                    Free shipping across India &nbsp;|&nbsp; Delivered in 5-7 business days
                  </p>
                </div>
              )}
            </div>
          )}

          {/* STEP 1.5: QR Code Link (Subscriptions only) */}
          {step === STEPS.QR_LINK && selectedPlan && (
            <div>
              {/* QRScanner overlay */}
              <QRScanner
                isOpen={showQRScanner}
                onScan={(scannedId) => {
                  setQrInput(scannedId.toUpperCase());
                  setQrValidation(null);
                  setError(null);
                  setShowQRScanner(false);
                  validateQrCode(scannedId.toUpperCase());
                }}
                onClose={() => setShowQRScanner(false)}
              />

              {/* Selected plan summary */}
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 mb-4 flex justify-between items-center">
                <div>
                  <p className="font-semibold text-slate-800 text-sm m-0">{selectedPlan.name}</p>
                  <p className="text-xs text-slate-500 m-0">{selectedPlan.description}</p>
                </div>
                <div className="text-right">
                  <span className="font-bold text-xl text-slate-900">{formatPrice(selectedPlan.price)}</span>
                  <p className="text-[11px] text-slate-400 m-0">/year</p>
                </div>
              </div>

              <p className="text-slate-600 text-sm mb-4 leading-relaxed">
                Link your premium subscription to a QR Code ID. Enter it manually or scan your sticker.
              </p>

              {/* Input + Scan button */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    QR Code ID <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={qrInput}
                      onChange={(e) => {
                        setQrInput(e.target.value.toUpperCase());
                        setQrValidation(null);
                        setError(null);
                      }}
                      onKeyDown={(e) => e.key === "Enter" && qrInput.trim() && validateQrCode()}
                      className={`${inputNormal} flex-1 uppercase tracking-wider font-mono`}
                      placeholder="e.g. ABC123XYZ"
                    />
                    <button
                      type="button"
                      onClick={() => validateQrCode()}
                      disabled={qrValidating || !qrInput.trim()}
                      className="px-4 py-2.5 bg-red-600 text-white rounded-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700 transition-colors duration-150 flex-shrink-0"
                    >
                      {qrValidating ? (
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />
                      ) : (
                        "Verify"
                      )}
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">ID is printed below the QR code on your sticker</p>
                </div>

                {/* Scan button */}
                <button
                  type="button"
                  onClick={() => setShowQRScanner(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-slate-300 text-slate-600 text-sm font-semibold hover:border-red-400 hover:text-red-700 hover:bg-red-50 transition-colors duration-150"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" /><path d="M14 14h2v2h-2zM18 14h3M14 18h2M18 18h3M21 21v-3" />
                  </svg>
                  Scan QR Code Instead
                </button>
              </div>

              {/* Validation result */}
              {qrValidation?.valid && !qrValidation?.hasActiveSubscription && !qrValidation?.suggestIndividual && (
                <div className="mt-4 p-3 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-green-600 text-lg font-bold">OK</span>
                    <span className="text-sm font-bold text-green-800">QR Code verified</span>
                  </div>
                  <p className="text-xs text-green-700 m-0">
                    <span className="font-mono font-bold">{qrInput}</span> is active and ready for premium.
                    {qrValidation.familyInfo && selectedPlan?.id === "family_yearly" && (
                      <> Family group: {qrValidation.familyInfo.membersWithQr} QR codes linked.</>
                    )}
                  </p>
                </div>
              )}

              {/* Suggest switching to Individual */}
              {qrValidation?.suggestIndividual && (
                <div className="mt-3">
                  <button
                    type="button"
                    onClick={handleSwitchToIndividual}
                    className="w-full p-3 rounded-lg bg-blue-50 border-2 border-blue-200 text-left hover:bg-blue-100 transition-colors duration-150"
                  >
                    <p className="text-sm font-bold text-blue-800 m-0">Switch to Individual Premium?</p>
                    <p className="text-xs text-blue-600 m-0 mt-0.5">
                      Individual Premium ({formatPrice(199)}/year) works with any single QR code.
                    </p>
                  </button>
                </div>
              )}

              {/* Continue button */}
              <div className="pt-4 mt-4 border-t border-slate-100">
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 border-2 border-slate-300 text-slate-700 font-semibold py-3 rounded-lg transition-colors duration-150 hover:bg-slate-50"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleQrLinkContinue}
                    disabled={!qrValidation?.valid || qrValidation?.hasActiveSubscription || qrValidation?.suggestIndividual}
                    className="flex-[2] bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Continue to Payment
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Address Form (Products only) */}
          {step === STEPS.ADDRESS_FORM && selectedPlan && (
            <div>
              {/* Selected plan summary */}
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 mb-4 flex justify-between items-center">
                <div>
                  <p className="font-semibold text-slate-800 text-sm m-0">{selectedPlan.name}</p>
                  <p className="text-xs text-slate-500 m-0">{selectedPlan.description}</p>
                </div>
                <span className="font-bold text-xl text-slate-900">{formatPrice(selectedPlan.price)}</span>
              </div>

              <form
                id="order-form"
                onSubmit={handleSubmit(() => {
                  setStep(STEPS.PAYMENT);
                  setError(null);
                })}
              >
                <div className="space-y-3">
                  {/* Name */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("name", { required: "Name is required" })}
                      className={errors.name ? inputError : inputNormal}
                      placeholder="Enter your full name"
                      autoComplete="name"
                    />
                    {errors.name && <span className="text-red-500 text-xs mt-0.5 block">{errors.name.message}</span>}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      {...register("phone", {
                        required: "Phone is required",
                        pattern: { value: /^[6-9]\d{9}$/, message: "Valid 10-digit Indian number required" },
                      })}
                      className={errors.phone ? inputError : inputNormal}
                      placeholder="10-digit number"
                      autoComplete="tel"
                    />
                    {errors.phone && <span className="text-red-500 text-xs mt-0.5 block">{errors.phone.message}</span>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      {...register("email", {
                        required: "Email is required",
                        pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: "Invalid email" },
                      })}
                      className={errors.email ? inputError : inputNormal}
                      placeholder="your@email.com"
                      autoComplete="email"
                    />
                    {errors.email && <span className="text-red-500 text-xs mt-0.5 block">{errors.email.message}</span>}
                  </div>

                  {/* Address Line 1 */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Address Line 1 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("addressLine1", { required: "Address is required" })}
                      className={errors.addressLine1 ? inputError : inputNormal}
                      placeholder="House/Flat No., Building"
                      autoComplete="address-line1"
                    />
                    {errors.addressLine1 && <span className="text-red-500 text-xs mt-0.5 block">{errors.addressLine1.message}</span>}
                  </div>

                  {/* Area/Colony */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Area/Colony <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("areaColony", { required: "Area/Colony is required" })}
                      className={errors.areaColony ? inputError : inputNormal}
                      placeholder="Area or Colony name"
                    />
                    {errors.areaColony && <span className="text-red-500 text-xs mt-0.5 block">{errors.areaColony.message}</span>}
                  </div>

                  {/* City */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("city", { required: "City is required" })}
                      className={errors.city ? inputError : inputNormal}
                      placeholder="City"
                    />
                    {errors.city && <span className="text-red-500 text-xs mt-0.5 block">{errors.city.message}</span>}
                  </div>

                  {/* State */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("state", { required: "State is required" })}
                      className={errors.state ? inputError : inputNormal}
                      placeholder="State"
                    />
                    {errors.state && <span className="text-red-500 text-xs mt-0.5 block">{errors.state.message}</span>}
                  </div>

                  {/* PIN Code */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      PIN Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      {...register("pinCode", {
                        required: "PIN Code is required",
                        pattern: { value: /^\d{6}$/, message: "Valid 6-digit PIN required" },
                      })}
                      className={errors.pinCode ? inputError : inputNormal}
                      placeholder="6-digit PIN"
                      autoComplete="postal-code"
                    />
                    {errors.pinCode && <span className="text-red-500 text-xs mt-0.5 block">{errors.pinCode.message}</span>}
                  </div>

                  {/* Optional fields toggle */}
                  <div>
                    <button
                      type="button"
                      onClick={() => setShowOptionalFields(!showOptionalFields)}
                      className="text-sm text-red-600 font-semibold transition-colors duration-150 hover:text-red-700"
                    >
                      {showOptionalFields ? "Hide optional fields" : "+ Add more address details"}
                    </button>
                  </div>

                  {/* Optional fields */}
                  {showOptionalFields && (
                    <>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Address Line 2</label>
                        <input
                          type="text"
                          {...register("addressLine2")}
                          className={inputNormal}
                          placeholder="Street, Road"
                          autoComplete="address-line2"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Address Line 3</label>
                        <input
                          type="text"
                          {...register("addressLine3")}
                          className={inputNormal}
                          placeholder="Additional details"
                          autoComplete="address-line3"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 mb-1">Landmark</label>
                        <input
                          type="text"
                          {...register("landmark")}
                          className={inputNormal}
                          placeholder="Nearby landmark"
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* Bottom buttons */}
                <div className="pt-4 mt-4 border-t border-slate-100">
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="flex-1 border-2 border-slate-300 text-slate-700 font-semibold py-3 rounded-lg transition-colors duration-150 hover:bg-slate-50"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="flex-[2] bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg transition-colors duration-150"
                    >
                      Continue to Payment
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}

          {/* STEP 3: Payment Method Selection */}
          {step === STEPS.PAYMENT && selectedPlan && (
            <div>
              {/* Order summary */}
              <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 mb-4">
                <h3 className="text-sm text-slate-600 font-semibold mb-2">
                  {isSubscription ? "Subscription Summary" : "Order Summary"}
                </h3>
                <div className="flex justify-between mb-1.5">
                  <span className="text-slate-700 text-sm">{selectedPlan.name}</span>
                  <span className="font-semibold text-slate-800 text-sm">{formatPrice(selectedPlan.price)}{isSubscription ? "/yr" : ""}</span>
                </div>
                {isSubscription && qrInput && (
                  <div className="flex justify-between mb-1.5">
                    <span className="text-slate-700 text-sm">QR Code</span>
                    <span className="font-mono font-semibold text-slate-800 text-sm">{qrInput}</span>
                  </div>
                )}
                {!isSubscription && (
                  <div className="flex justify-between mb-1.5">
                    <span className="text-slate-700 text-sm">Shipping</span>
                    <span className="font-semibold text-green-600 text-sm">FREE</span>
                  </div>
                )}
                <hr className="border-slate-200 my-2.5" />
                <div className="flex justify-between items-center">
                  <span className="font-bold text-slate-900 text-base">Total</span>
                  <span className="font-extrabold text-slate-900 text-xl">{formatPrice(selectedPlan.price)}{isSubscription ? "/yr" : ""}</span>
                </div>
              </div>

              {/* Test mode indicator */}
              {paymentConfig?.isTestMode && (
                <div className="px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-400 mb-3 text-center">
                  <p className="text-xs text-yellow-800 font-semibold m-0">
                    Test Mode -- No real charges. Use test card: 4111 1111 1111 1111
                  </p>
                </div>
              )}

              {/* Payment methods */}
              <div className="flex flex-col gap-2.5">
                {paymentMethods.map((method) => {
                  const isSelected = selectedPaymentMethod === method.id;
                  const isProcessing = paymentProcessing && isSelected;
                  const isDisabled = paymentProcessing && !isSelected;

                  return (
                    <button
                      key={method.id}
                      onClick={() => processPayment(method.id)}
                      disabled={paymentProcessing}
                      className={`relative flex items-center gap-3.5 p-3 rounded-lg border-2 text-left transition-colors duration-150 min-h-[64px] ${
                        isSelected
                          ? "border-red-500 bg-red-50/50"
                          : "border-slate-200 bg-white hover:border-red-400 hover:bg-red-50/30"
                      } ${isDisabled ? "opacity-50 cursor-not-allowed" : ""} ${
                        isProcessing ? "cursor-wait" : paymentProcessing ? "" : "cursor-pointer"
                      }`}
                    >
                      {/* Icon */}
                      <div className="w-11 h-11 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 flex-shrink-0">
                        {method.icon}
                      </div>

                      {/* Label */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[15px] font-bold text-slate-900">{method.label}</span>
                          {method.recommended && (
                            <span className="text-[10px] font-bold text-white bg-green-600 px-2 py-0.5 rounded-lg uppercase tracking-wide">
                              Popular
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-slate-500 mt-0.5 block">{method.sublabel}</span>
                      </div>

                      {/* Arrow or spinner */}
                      <div className="flex-shrink-0 text-slate-400">
                        {isProcessing ? (
                          <div className="w-5 h-5 border-2 border-slate-300 border-t-red-600 rounded-full animate-spin" />
                        ) : (
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path
                              d="M7 4L13 10L7 16"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Back button */}
              <button
                onClick={handleBack}
                disabled={paymentProcessing}
                className={`w-full mt-3 border-2 border-slate-300 text-slate-700 font-semibold py-3 rounded-lg text-sm transition-colors duration-150 hover:bg-slate-50 ${
                  paymentProcessing ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                Back to {isSubscription ? "QR Code" : "Shipping Details"}
              </button>

              {/* Security note */}
              <div className="flex items-center justify-center gap-1.5 mt-3">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="11" width="18" height="11" rx="2" stroke="#94a3b8" strokeWidth="1.5" />
                  <path d="M7 11V7a5 5 0 0110 0v4" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <span className="text-[11px] text-slate-400">
                  Payments are 100% secure and encrypted
                </span>
              </div>
            </div>
          )}

          {/* STEP 4: Success */}
          {step === STEPS.SUCCESS && orderResult && (
            <div className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">
                {isSubscription ? "Premium Activated!" : "Thank You!"}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-4 max-w-[400px] mx-auto">
                {orderResult.message}
              </p>
              {orderResult.paymentId && (
                <div className="bg-slate-50 rounded-lg p-2.5 mb-3">
                  <p className="text-xs text-slate-500 m-0">
                    Payment ID: <span className="font-mono font-semibold">{orderResult.paymentId}</span>
                  </p>
                </div>
              )}
              {orderResult.orderId && (
                <div className="bg-slate-50 rounded-lg p-2.5 mb-3">
                  <p className="text-xs text-slate-500 m-0">
                    Order ID: <span className="font-mono font-semibold">{orderResult.orderId}</span>
                  </p>
                </div>
              )}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-green-700 font-medium m-0">
                  {isSubscription
                    ? "Your premium features are now active. Enjoy unlimited fields, scan notifications, and more!"
                    : "You'll receive a confirmation on your email and phone shortly."}
                </p>
              </div>
              <button
                onClick={() => navigate("/")}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg text-base transition-colors duration-150"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Language Switcher - fixed bottom-right */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-1">
        <LanguageSwitcher />
      </div>
    </div>
  );
};

export default OrderPage;
